import React from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';

import { EditWeekOrder } from './components';
import OrderItem from 'onramp/ui/components/OrderItem';
import { Box, Button, Flex, Icon, Text, Line } from 'core/ui/atoms';

import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import {
  GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY,
  GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY,
} from 'core/api/apollo/queries';

const OrderList = ({ deliveryPeriod }) => {
  const variables = { deliveryDayId: deliveryPeriod?.id };

  const { loading, error, data: {
    getProductComponentsByDeliveryDay = [] } = {},
  } = useQuery(GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY, {
    variables,
  });

  const {
    data: { getSwappedProductComponentsByDeliveryDay = [] } = {},
  } = useQuery(GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY, {
    variables,
    skip: loading,
  });

  if (isLoading(loading, getProductComponentsByDeliveryDay, error)) {
    return <Loading />;
  }

  const groupProductByDay = groupBy(getProductComponentsByDeliveryDay, 'day');

  if (isEmpty(groupProductByDay)) {
    return (
      <Container>
        <EmptyMessage>No Order generated for the week</EmptyMessage>
      </Container>
    );
  }

  const dayList = Object.keys(groupProductByDay).filter((data) => data !== 'null');

  const renderOrderItem = (orderItems) => {
    return orderItems.map((item, index) => {
      const swappedItem = getSwappedProductComponentsByDeliveryDay.find((swapped) => swapped.originalComponentId === item.id);
      if (swappedItem) {
        const swappedItemWithFlag = { ...swappedItem, swapped: true, editable: false };
        return <OrderItem item={swappedItemWithFlag} key={index} deliveryDayId={deliveryPeriod?.id} />;
      }
      return <OrderItem item={item} key={index} deliveryDayId={deliveryPeriod?.id} />;
    });
  };

  const scrollRefs = Array(dayList.length).fill().map(() => React.createRef());

  const RenderItem = () => {
    return dayList.map((dayItem, index) => {
      const scrollRef = scrollRefs[index];

      const scrollLeft = () => {
        if (scrollRef.current) {
          scrollRef.current.scrollBy({
            left: -300, // Adjust scroll distance as needed
            behavior: 'smooth',
          });
        }
      };

      const scrollRight = () => {
        if (scrollRef.current) {
          scrollRef.current.scrollBy({
            left: 300, // Adjust scroll distance as needed
            behavior: 'smooth',
          });
        }
      };

      return (
        <Box key={index}>
          <Flex direction='row' alignItems='center' justifyContent='center' gap='16px'>
            <Line />
            <DayText>{`Day ${dayItem}`}</DayText>
            <Line />
          </Flex>
          <ScrollContainer>
            <CaretButton btn-unstyled onClick={scrollLeft} position="left">
              <Caret icon='arrow_l' />
            </CaretButton>
            <ScrollableContent ref={scrollRef}>
              {renderOrderItem(groupProductByDay[dayItem])}
            </ScrollableContent>
            <CaretButton btn-unstyled onClick={scrollRight} position="right">
              <Caret icon='arrow_r' />
            </CaretButton>
          </ScrollContainer>
        </Box>
      );
    });
  };

  return (
    <Container>
      <RenderItem />
      <MobileContainer>
        <EditWeekOrder
          deliveryPeriodId={deliveryPeriod?.id}
        />
      </MobileContainer>
    </Container>
  );
};

OrderList.propTypes = {
  deliveryPeriod: PropTypes.object.isRequired,
};

const MobileContainer = styled.div`
  display: none;
  ${(p) => p.theme.max('md')`
    display: block;
  `}
`;

const CaretButton = styled(Button)`
  color: ${({ theme }) => theme.colors.primary};
  ${(p) => p.theme.max('sm')`
    display: none;
  `}
`;

const Caret = styled(Icon)`
  display: inline-block;
  transition: transform 0.25s ease-in-out;
  transform-style: preserve-3d;
  transform: rotateX(${(p) => p.$open ? '180deg' : '0deg' });
`;

const DayText = styled(Text)`
  text-align: center;
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const ScrollContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  width: 100%;
  gap: 24px;
  padding: 20px 60px;
  scrollbar-width: none;
  overflow-y: visible;
  column-gap: 25px;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: auto;

  & > * {
    scroll-snap-align: center;
    scroll-snap-stop: normal;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    height: 6px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* For Firefox */
  scrollbar-color: #888 #f1f1f1;
  ${(p) => p.theme.max('sm')`
    padding-right: 0px;
    padding-left: 0px;
    padding-bottom: 20px;
  `}
`;

const Container = styled.div`
  margin-top: 15px;
  width: 95%;
  overflow: hidden;
`;

const EmptyMessage = styled(Text)`
  text-align: center;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 22px;
  line-height: 28px;
  margin-top: 40px;
  margin-bottom: 40px;
`;

export default OrderList;
